import React from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import { OlsConfigStore } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import {
  AddToCartButton,
  ApplePayButton,
  PaymentRequestButton,
  PoyntPaymentRequestButton,
  BuyNowButton
} from '../../';
import Fetching from '../../Fetching';
import dataAids from '../../../../constants/dataAids';
import { DigitalWalletStore } from '../../../utils/ols-core';
import { theme } from '../../utils/ProductUtils';
import { getIsLowInventoryInGallery } from './lowInventoryHelper';

class CheckoutButtons extends React.Component {
  constructor() {
    super(...arguments);
    this.state = {
      matchingButtonId: null,
      disabledCartButton: false,
      ...this.getStoreState()
    };
  }

  getStoreState() {
    const { inSession } = DigitalWalletStore.getState();
    const { configWasLoaded, buyNowEnabled } = OlsConfigStore.getState();

    return { inSession, configWasLoaded, buyNowEnabled };
  }

  componentDidMount() {
    DigitalWalletStore.addListener('change', this.onStoreChange);
    OlsConfigStore.addListener('change', this.onStoreChange);
    this.setState({ matchingButtonId: `#addtocart-btn-${this.props.product.id}` });
  }

  componentWillUnmount() {
    DigitalWalletStore.removeListener('change', this.onStoreChange);
    OlsConfigStore.removeListener('change', this.onStoreChange);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.disabledCartButton &&
      (prevProps.requiredFieldsBlank !== this.props.requiredFieldsBlank ||
        prevProps.quantity !== this.props.quantity)
    ) {
      this.setState({ disabledCartButton: false });
    }
  }

  onStoreChange = () => {
    this.setState(this.getStoreState());
  };

  render() {
    const {
      handleAddToCart,
      handleApplePayClick,
      getOrderData,
      phoneRequired,
      translate,
      renderMode,
      category,
      prices,
      applePayIsAvailable,
      requiredFieldsBlank,
      processing,
      isSelectedVariantInStock,
      isPaymentRequestEnabled,
      isPoyntPaymentRequestEnabled,
      stackButtons,
      hasAddedToCartLabel,
      quantity,
      setHasAttemptedPurchase,
      isQuickView,
      checkoutStyles,
      AdditionalButton,
      isGalleryDetail,
      lowInventoryExperiment,
      product,
      priceTable,
      selectedVariant,
      isLowInventoryInGalleryEnabled,
      metricTrackRef,
      shouldStickToBottom
    } = this.props;

    const buttonStyles = {
      '@xs': {
        borderRadius: '5px',
        paddingLeft: 'xsmall',
        paddingRight: 'xsmall',
        paddingTop: 'small',
        paddingBottom: 'small',
        lineHeight: '18px'
      },
      '@sm': {
        paddingLeft: 'medium',
        paddingRight: 'medium'
      }
    };

    const { Block } = UX2.Element;
    const { matchingButtonId, inSession, disabledCartButton } = this.state;

    const price = prices.onSale ? prices.salePrice : prices.price;
    const showApplePayButton = applePayIsAvailable && price > 0.0;
    const isEditMode =
      renderMode === 'EDIT' ||
      renderMode === 'PREVIEW' ||
      (isQuickView && renderMode !== 'PUBLISH');
    const disabled =
      processing ||
      inSession ||
      !isSelectedVariantInStock ||
      requiredFieldsBlank ||
      isEditMode ||
      !quantity;
    const buttonState =
      isSelectedVariantInStock &&
      !disabledCartButton &&
      !requiredFieldsBlank &&
      !inSession &&
      !isEditMode
        ? 'default'
        : 'disabled';

    const addToCartText = hasAddedToCartLabel
      ? translate('products.added_to_cart')
      : translate('products.add_to_cart');

    const buttonProps = showApplePayButton
      ? {
        style: buttonStyles
      }
      : null;

    const handleAddToCartClick = () => {
      if (isEditMode) return;

      this.setState({ disabledCartButton: true });
      handleAddToCart(isLowInventoryInGalleryEnabled);
    };

    const digitalWalletButton = () => {
      if (isPaymentRequestEnabled) {
        const styles = {
          paymentRequestButton: {
            theme: theme({ isQuickView, category }),
            type: 'buy',
            height: '50px'
          }
        };
        return (
          <PaymentRequestButton
            renderMode={ renderMode }
            initPage='product_detail'
            styles={ styles }
            matchingButtonId={ matchingButtonId }
            disabled={ disabled }
            getOrderData={ getOrderData }
            phoneRequired={ phoneRequired }
            dataAid={ dataAids.PDP_PAYMENT_REQUEST }
            isGalleryDetail={ isGalleryDetail }
            productId={ product?.id }
          />
        );
      }
      return (
        <ApplePayButton
          category={ category }
          renderMode={ renderMode }
          initPage='product_detail'
          disabled={ disabled }
          onClick={ handleApplePayClick }
          data-aid={ dataAids.PDP_APPLE_PAY }
          productId={ product?.id }
        />
      );
    };

    const buyNowAvailable =
      this.state.buyNowEnabled && !isPoyntPaymentRequestEnabled && !isPaymentRequestEnabled;
    const buyNowFlexDirection = buyNowAvailable ? 'row-reverse' : 'row';
    const checkoutButtonStyles = stackButtons
      ? { flexDirection: 'column', alignItems: 'flex-start' }
      : { flexDirection: buyNowFlexDirection, alignItems: 'center' };

    const styles = {
      wrapper: {
        'display': 'flex',
        'gap': buyNowAvailable ? '24px' : '0',
        'justifyContent': buyNowAvailable ? 'flex-end' : 'flex-start',
        'marginBottom': shouldStickToBottom ? 'none' : 'small',
        '@xs-only': {
          display: 'flex',
          justifyContent: 'flex-start',
          flexDirection: 'column',
          gap: '0px',
          width: '100%'
        },
        ...checkoutButtonStyles
      },
      addToCart: {
        'marginLeft': '0',
        '@xs-only': {
          width: '100%'
        }
      },
      digitalWalletWrapper: {
        'marginLeft': stackButtons ? '0' : 'medium',
        'marginTop': stackButtons ? 'small' : '0',
        'minWidth': '160px',
        '@xs-only': {
          marginLeft: '0',
          width: '100%',
          marginTop: '12px'
        }
      }
    };

    if (!this.state.configWasLoaded) {
      return (
        <Fetching
          fetching={ !this.state.configWasLoaded }
          pageTransition={ false }
          transparentBackground={ false }
        />
      );
    }

    const responsiveStyles = {
      'display': 'flex',
      'gap': '24px',
      'marginBottom': shouldStickToBottom ? 'none' : 'large',
      '@sm': {
        flexDirection: stackButtons ? 'column' : 'row',
        alignItems: stackButtons ? 'flex-start' : 'center',
        justifyContent: 'flex-start',
        flexWrap: 'wrap'
      },
      '@xs-only': {
        flexDirection: isPoyntPaymentRequestEnabled ? 'column-reverse' : 'column',
        marginBottom: shouldStickToBottom ? 'none' : 'large',
        gap: '0px'
      },
      ...checkoutStyles
    };

    const { bucket, error } = lowInventoryExperiment;
    const lowInventoryThreshold = product?.low_inventory_threshold || 3;
    const variantData = priceTable[selectedVariant] || {};

    const hasLowInventory =
      variantData.totalOnHand <= lowInventoryThreshold && variantData.totalOnHand > 0;
    const experimentEnabled = bucket && bucket !== 'inelegible' && !error && hasLowInventory;
    const isLowInventoryInGallery = getIsLowInventoryInGallery(product);

    if (isPoyntPaymentRequestEnabled) {
      return (
        <Block style={ responsiveStyles }>
          <Block>
            <AddToCartButton
              state={ buttonState }
              onClick={ handleAddToCartClick }
              disabled={ disabled }
              productId={ product?.id }
              { ...buttonProps }
              shouldStickToBottom={ shouldStickToBottom }
            >
              { addToCartText }
            </AddToCartButton>
          </Block>
          { showApplePayButton &&
            !isEditMode &&
            product &&
            (typeof product.id === 'string' || typeof product.id === 'number') && (
            <Block
              style={{
                '@xs-only': {
                  marginBottom: shouldStickToBottom ? '10px' : 'small',
                  width: '100%'
                }
              }}
            >
              <PoyntPaymentRequestButton
                data-aid={ dataAids.PDP_POYNT_PAYMENT_REQUEST }
                productId={ product?.id }
                initPage='product_detail'
                theme={ theme({ isQuickView, category }) }
                disabled={ disabled }
                getOrderData={ getOrderData }
                phoneRequired={ phoneRequired }
                setHasAttemptedPurchase={ setHasAttemptedPurchase }
                experimentEnabled={ experimentEnabled }
                isQuickView={ isQuickView }
                isLowInventoryInGalleryEnabled={ isLowInventoryInGalleryEnabled }
                isLowInventoryInGallery={ isLowInventoryInGallery }
                isGalleryDetail={ isGalleryDetail }
                shouldStickToBottom={ shouldStickToBottom }
              />
            </Block>
          ) }
          { AdditionalButton && AdditionalButton }
        </Block>
      );
    }

    if (showApplePayButton) {
      return (
        <>
          <Block style={ styles.wrapper }>
            <Block style={ styles.addToCart }>
              <AddToCartButton
                state={ buttonState }
                onClick={ handleAddToCartClick }
                disabled={ disabled }
                productId={ product?.id }
                shouldStickToBottom={ shouldStickToBottom }
              >
                { addToCartText }
              </AddToCartButton>
            </Block>
            <Block children={ digitalWalletButton() } style={ styles.digitalWalletWrapper } />
          </Block>
          { AdditionalButton && AdditionalButton }
        </>
      );
    }

    if (buyNowAvailable) {
      return (
        <Block style={ responsiveStyles }>
          <Block
            style={{
              '@xs-only': {
                marginBottom: shouldStickToBottom ? '10px' : 'small',
                width: '100%'
              }
            }}
          >
            <BuyNowButton
              productId={ product?.id }
              setHasAttemptedPurchase={ setHasAttemptedPurchase }
              isEditMode={ isEditMode }
              disabled={ disabled }
              state={ buttonState }
              getOrderData={ getOrderData }
              translate={ translate }
              isGalleryDetail={ isGalleryDetail }
              experimentEnabled={ experimentEnabled }
              isQuickView={ isQuickView }
              isLowInventoryInGalleryEnabled={ isLowInventoryInGalleryEnabled }
              isLowInventoryInGallery={ isLowInventoryInGallery }
              metricTrackRef={ metricTrackRef }
            />
          </Block>
          <Block>
            <AddToCartButton
              state={ buttonState }
              onClick={ handleAddToCartClick }
              disabled={ disabled }
              { ...buttonProps }
              productId={ product?.id }
              shouldStickToBottom={ shouldStickToBottom }
            >
              { addToCartText }
            </AddToCartButton>
          </Block>
          { AdditionalButton && AdditionalButton }
        </Block>
      );
    }

    return (
      <Block style={ responsiveStyles }>
        <Block>
          <AddToCartButton
            state={ buttonState }
            onClick={ handleAddToCartClick }
            disabled={ disabled }
            { ...buttonProps }
            productId={ product?.id }
            shouldStickToBottom={ shouldStickToBottom }
          >
            { addToCartText }
          </AddToCartButton>
        </Block>
        { AdditionalButton && AdditionalButton }
      </Block>
    );
  }
}

CheckoutButtons.propTypes = {
  handleAddToCart: PropTypes.func,
  handleApplePayClick: PropTypes.func,
  getOrderData: PropTypes.func,
  phoneRequired: PropTypes.bool,
  translate: PropTypes.func,
  renderMode: PropTypes.string,
  category: PropTypes.string,
  prices: PropTypes.object,
  applePayIsAvailable: PropTypes.bool,
  requiredFieldsBlank: PropTypes.bool,
  processing: PropTypes.bool,
  isSelectedVariantInStock: PropTypes.bool,
  isPaymentRequestEnabled: PropTypes.bool,
  isPoyntPaymentRequestEnabled: PropTypes.bool,
  stackButtons: PropTypes.bool,
  hasAddedToCartLabel: PropTypes.bool,
  quantity: PropTypes.string,
  setHasAttemptedPurchase: PropTypes.func,
  isQuickView: PropTypes.bool,
  checkoutStyles: PropTypes.object,
  AdditionalButton: PropTypes.element,
  isGalleryDetail: PropTypes.bool,
  lowInventoryExperiment: PropTypes.object,
  product: PropTypes.object,
  priceTable: PropTypes.object,
  selectedVariant: PropTypes.number,
  isLowInventoryInGalleryEnabled: PropTypes.bool,
  metricTrackRef: PropTypes.object,
  shouldStickToBottom: PropTypes.bool
};

export default CheckoutButtons;
